import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import cytoscape from 'cytoscape';
import fcose from 'cytoscape-fcose';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { ANNOTATION_COLORS, getAnnotationColor } from '../../annotationColors';

cytoscape.use(fcose);


// ─── AA classification ────────────────────────────────────────────────────────
const AA_CATEGORY = {
  ALA: 'Non-polar', CYS: 'Non-polar', GLY: 'Non-polar', ILE: 'Non-polar',
  LEU: 'Non-polar', MET: 'Non-polar', PHE: 'Non-polar', PRO: 'Non-polar',
  TRP: 'Non-polar', VAL: 'Non-polar',
  ASN: 'Polar', GLN: 'Polar', SER: 'Polar', THR: 'Polar', TYR: 'Polar',
  ARG: 'Positive', HIS: 'Positive', LYS: 'Positive',
  ASP: 'Negative', GLU: 'Negative',
};
const CATEGORY_COLORS = {
  'Non-polar': '#f4d03f', 'Polar': '#5dade2',
  'Positive':  '#58d68d', 'Negative': '#bb8fce',
};
const CATEGORY_TEXT_COLORS = {
  'Non-polar': '#5a4e00', 'Polar': '#ffffff',
  'Positive':  '#ffffff', 'Negative': '#ffffff',
};
const CATEGORY_BORDER_COLORS = {
  'Non-polar': '#c9a800', 'Polar': '#2e86c1',
  'Positive':  '#1e8449', 'Negative': '#8e44ad',
};
const CATEGORY_LABEL = {
  'Non-polar': 'Non-polar', 'Polar': 'Polar',
  'Positive': 'Positive (+)', 'Negative': 'Negative (−)',
};
const CATEGORY_AAS = {
  'Non-polar': ['ALA','CYS','GLY','ILE','LEU','MET','PHE','PRO','TRP','VAL'],
  'Polar':     ['ASN','GLN','SER','THR','TYR'],
  'Positive':  ['ARG','HIS','LYS'],
  'Negative':  ['ASP','GLU'],
};

// ─── Kyte-Doolittle ───────────────────────────────────────────────────────────
const KD_SCALE = {
  ILE: 4.5, VAL: 4.2, LEU: 3.8, PHE: 2.8, CYS: 2.5,
  MET: 1.9, ALA: 1.8, GLY: -0.4, THR: -0.7, SER: -0.8,
  TRP: -0.9, TYR: -1.3, PRO: -1.6, HIS: -3.2, GLU: -3.5,
  GLN: -3.5, ASP: -3.5, ASN: -3.5, LYS: -3.9, ARG: -4.5,
};
const KD_MIN = -4.5, KD_MAX = 4.5;

function kdToColor(aa) {
  const kd = KD_SCALE[(aa || '').toUpperCase()];
  if (kd === undefined) return '#cccccc';
  const t = (kd - KD_MIN) / (KD_MAX - KD_MIN);
  let r, g, b;
  if (t < 0.35)      { const s=t/0.35;           r=Math.round(31+s*(166-31));  g=Math.round(120+s*(206-120)); b=Math.round(180+s*(227-180)); }
  else if (t < 0.5)  { const s=(t-0.35)/0.15;    r=Math.round(166+s*(247-166));g=Math.round(206+s*(247-206));b=Math.round(227+s*(247-227)); }
  else if (t < 0.65) { const s=(t-0.5)/0.15;     r=Math.round(247+s*(253-247));g=Math.round(247+s*(174-247));b=Math.round(247+s*(97-247));  }
  else               { const s=(t-0.65)/0.35;     r=Math.round(253+s*(215-253));g=Math.round(174+s*(48-174)); b=Math.round(97+s*(39-97));   }
  return `rgb(${r},${g},${b})`;
}
function kdToTextColor(aa) {
  const kd = KD_SCALE[(aa||'').toUpperCase()];
  if (kd===undefined) return '#333';
  const t=(kd-KD_MIN)/(KD_MAX-KD_MIN);
  return (t>0.25&&t<0.75)?'#333333':'#ffffff';
}
function kdToBorderColor(aa) {
  const kd = KD_SCALE[(aa||'').toUpperCase()];
  if (kd===undefined) return '#aaa';
  const t=(kd-KD_MIN)/(KD_MAX-KD_MIN);
  if (t<0.35) return '#0e4d7a';
  if (t<0.5)  return '#5b9ebd';
  if (t<0.65) return '#c47a30';
  return '#8b1a1a';
}

// ─── Metric color scale (white → indigo) ─────────────────────────────────────
function metricToColor(norm) {
  const t = Math.min(Math.max(norm, 0), 1);
  return `rgb(${Math.round(255-t*(255-63))},${Math.round(255-t*(255-84))},${Math.round(255-t*(255-196))})`;
}
function metricTextColor(norm)   { return norm > 0.5 ? '#ffffff' : '#333333'; }
function metricBorderColor(norm) {
  const t = Math.min(Math.max(norm, 0), 1);
  return `rgb(${Math.round(40-t*10)},${Math.round(100-t*60)},${Math.round(180-t*60)})`;
}

const AA_THREE_TO_ONE = {
  ALA:'A',ARG:'R',ASN:'N',ASP:'D',CYS:'C',GLN:'Q',GLU:'E',GLY:'G',
  HIS:'H',ILE:'I',LEU:'L',LYS:'K',MET:'M',PHE:'F',PRO:'P',SER:'S',
  THR:'T',TRP:'W',TYR:'Y',VAL:'V',
};


// ─── Parse ligand details: "A/14/CA/GLY | A/201/O1B/GDP" ─────────────────────
// Returns { residue: {chain,pos,atom,aa}, ligand: {chain,pos,atom,name} }
function parseLigandDetails(details) {
  if (!details) return null;
  const parts = details.split('|').map(p => {
    const tokens = p.trim().split('/');
    if (tokens.length >= 4) {
      return { chain: tokens[0].trim(), pos: parseInt(tokens[1],10), atom: tokens[2].trim(), name: tokens[3].trim().toUpperCase() };
    }
    return null;
  }).filter(Boolean);

  if (parts.length < 2) return null;

  // Heuristic: residue part has standard AA name (3-letter), ligand part has het code
  const isAA = (name) => name in AA_CATEGORY;
  const residuePart = parts.find(p => isAA(p.name));
  const ligandPart  = parts.find(p => !isAA(p.name));

  if (!residuePart || !ligandPart) return null;
  return { residue: residuePart, ligand: ligandPart };
}

// ─── Build elements ───────────────────────────────────────────────────────────
function buildElements(ligandData) {
  const residueMap = new Map(); // key: "chain+pos+aa"
  const ligandMap  = new Map(); // key: ligandName (het code)
  const edges      = [];

  // Debug: log first item to check data shape
  if (ligandData.length > 0) {
    console.log('[LigandNetwork] sample item:', JSON.stringify(ligandData[0]));
  }

  ligandData.forEach((item, idx) => {
    const parsed = parseLigandDetails(item.details);
    if (!parsed) return;

    const { residue, ligand } = parsed;
    const aa  = residue.name.toUpperCase();
    const cat = AA_CATEGORY[aa] || 'Non-polar';
    const rId = `R_${residue.chain}${residue.pos}_${aa}`;
    const lId = `L_${ligand.name}`;

    if (!residueMap.has(rId)) {
      // anPosition: number or "UNIPROTID_SEQ" string
      const seqPos = item.anPosition != null
        ? (typeof item.anPosition === 'string' && item.anPosition.includes('_')
            ? parseInt(item.anPosition.split('_')[1], 10)
            : parseInt(item.anPosition, 10))
        : null;
      residueMap.set(rId, {
        id: rId, nodeType: 'residue',
        label: `${AA_THREE_TO_ONE[aa] || aa}${residue.pos}`,
        aa, chain: residue.chain, pos: residue.pos,
        seqPos,
        category: cat,
        kd: KD_SCALE[aa] ?? 0,
        metricBC: 0, metricCC: 0, metricDeg: 0, metricNorm: 0,
      });
    }

    const ligandName = item.ligand || ligand.name;
    const annotation = item.annotation || 'Unannotated';
    if (!ligandMap.has(lId)) {
      ligandMap.set(lId, {
        id: lId, nodeType: 'ligand',
        label: ligandName,
        ligandName, annotation,
        pdbId: item.pdbId || '',
      });
    }

    const edgeKey = `${rId}--${lId}`;
    // Deduplicate by residue+ligand pair, keep shortest distance
    const dist = parseFloat(item.distance) || 5;
    const existing = edges.find(e => e.data.id === edgeKey);
    if (!existing) {
      edges.push({ data: { id: edgeKey, source: rId, target: lId, distance: dist } });
    } else if (dist < existing.data.distance) {
      existing.data.distance = dist;
    }
  });

  const residueNodes = Array.from(residueMap.values()).map(n => ({ data: n }));
  const ligandNodes  = Array.from(ligandMap.values()).map(n => ({ data: n }));
  return [...residueNodes, ...ligandNodes, ...edges];
}

// ─── Distance helpers ─────────────────────────────────────────────────────────
function distToWidth(d) {
  const c = Math.min(Math.max(d, 3.5), 8);
  return 5 - ((c - 3.5) / 4.5) * 3.5;
}
function distToOpacity(d) {
  const c = Math.min(Math.max(d, 3.5), 8);
  return 0.85 - ((c - 3.5) / 4.5) * 0.55;
}
function distToEdgeColor(d) {
  const t = Math.min(Math.max((d - 3.5) / 4.5, 0), 1);
  const v = Math.round(85 + t * 100);
  return `rgb(${v},${v},${v})`;
}

// ─── Stylesheet ───────────────────────────────────────────────────────────────
function buildStylesheet(colorMode) {
  const isCat   = colorMode === 'category';
  const isHydro = colorMode === 'hydrophobicity';
  return [
    // Base node style — prevents Cytoscape default grey overriding mappers
    {
      selector: 'node',
      style: {
        'font-size': '9px', 'font-family': 'ui-monospace, monospace',
        'font-weight': '700', 'text-valign': 'center', 'text-halign': 'center',
        'border-width': 1.5, 'border-opacity': 0.8,
        'transition-property': 'width, height, border-width',
        'transition-duration': '0.15s',
      }
    },
    // Residue nodes
    {
      selector: 'node[nodeType = "residue"]',
      style: {
        'label':            'data(label)',
        'shape':            'ellipse',
        'background-color': isCat   ? (ele) => CATEGORY_COLORS[ele.data('category')]       || '#f4d03f'
                          : isHydro ? (ele) => kdToColor(ele.data('aa'))
                                    : (ele) => metricToColor(ele.data('metricNorm') ?? 0),
        'color':            isCat   ? (ele) => CATEGORY_TEXT_COLORS[ele.data('category')]  || '#5a4e00'
                          : isHydro ? (ele) => kdToTextColor(ele.data('aa'))
                                    : (ele) => metricTextColor(ele.data('metricNorm') ?? 0),
        'border-color':     isCat   ? (ele) => CATEGORY_BORDER_COLORS[ele.data('category')]|| '#c9a800'
                          : isHydro ? (ele) => kdToBorderColor(ele.data('aa'))
                                    : (ele) => metricBorderColor(ele.data('metricNorm') ?? 0),
        'font-size': '9px', 'font-family': 'ui-monospace, monospace',
        'font-weight': '700', 'text-valign': 'center', 'text-halign': 'center',
        'width': 28, 'height': 28, 'border-width': 1.5, 'border-opacity': 0.8,
        'transition-property': 'width, height, border-width',
        'transition-duration': '0.15s',
      }
    },
    // Ligand nodes — diamond shape, annotation color
    {
      selector: 'node[nodeType = "ligand"]',
      style: {
        'label':            'data(label)',
        'shape':            'diamond',
        'background-color': (ele) => getAnnotationColor(ele.data('annotation')),
        'color':            '#ffffff',
        'border-color':     (ele) => getAnnotationColor(ele.data('annotation')),
        'font-size': '10px', 'font-family': 'sans-serif',
        'font-weight': '700', 'text-valign': 'center', 'text-halign': 'center',
        'width': 38, 'height': 38, 'border-width': 2, 'border-opacity': 0.9,
        'transition-property': 'width, height',
        'transition-duration': '0.15s',
      }
    },
    { selector: 'node.highlighted', style: { 'border-width': 3, 'border-opacity': 1, 'width': (ele) => ele.data('nodeType')==='ligand'?46:34, 'height': (ele) => ele.data('nodeType')==='ligand'?46:34, 'z-index': 9 } },
    { selector: 'node.dimmed',      style: { 'opacity': 0.12 } },
    {
      selector: 'edge',
      style: {
        'width':      (ele) => distToWidth(ele.data('distance')),
        'line-color': (ele) => distToEdgeColor(ele.data('distance')),
        'opacity':    (ele) => distToOpacity(ele.data('distance')),
        'curve-style': 'bezier', 'target-arrow-shape': 'none',
      }
    },
    { selector: 'edge.hidden',      style: { 'display': 'none' } },
    { selector: 'edge.dimmed',      style: { 'opacity': 0.04 } },
    { selector: 'edge.highlighted', style: { 'opacity': 0.9, 'width': (ele) => distToWidth(ele.data('distance')) + 1 } },
  ];
}

const LAYOUT_OPTIONS = [
  { label: 'fCoSE (force-directed)', value: 'fcose' },
  { label: 'Concentric',             value: 'concentric' },
  { label: 'Circle',                 value: 'circle' },
  { label: 'Grid',                   value: 'grid' },
];

function getLayoutConfig(name) {
  switch (name) {
    case 'concentric': return { name: 'concentric', minNodeSpacing: 20, levelWidth: () => 2 };
    case 'circle':     return { name: 'circle',     padding: 40 };
    case 'grid':       return { name: 'grid',       padding: 40 };
    default: return {
      name: 'fcose', quality: 'default', randomize: true, animate: true,
      animationDuration: 600, nodeSeparation: 80, idealEdgeLength: 90,
      nodeRepulsion: 5000, padding: 30,
    };
  }
}

// ─── KD colorbar (unique id per instance) ────────────────────────────────────
function KDColorbar({ id }) {
  const gradId = `${id}-kd-gradient`;
  const stops = [
    { pct: 0, color: '#1f78b4' }, { pct: 35, color: '#a6cee3' },
    { pct: 50, color: '#f7f7f7' }, { pct: 65, color: '#fdae61' },
    { pct: 100, color: '#d73027' },
  ];
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', fontSize:10, color:'#444', gap:2 }}>
      <span style={{ fontWeight:600, color:'#222', marginBottom:2 }}>Kyte-Doolittle</span>
      <div style={{ display:'flex', alignItems:'center', gap:4 }}>
        <span style={{ color:'#1f78b4', fontWeight:600 }}>hydrophilic</span>
        <svg width="120" height="12">
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
              {stops.map(s => <stop key={s.pct} offset={`${s.pct}%`} stopColor={s.color} />)}
            </linearGradient>
          </defs>
          <rect x="0" y="0" width="120" height="12" fill={`url(#${gradId})`} rx="3" />
        </svg>
        <span style={{ color:'#d73027', fontWeight:600 }}>hydrophobic</span>
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', width:120, marginLeft:74 }}>
        <span>−4.5</span><span>0</span><span>+4.5</span>
      </div>
    </div>
  );
}

// ─── Metric Colorbar ─────────────────────────────────────────────────────────
function MetricColorbar({ label, id }) {
  const gradId = `${id}-metric-gradient`;
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', fontSize:10, color:'#444', gap:2 }}>
      <span style={{ fontWeight:600, color:'#222', marginBottom:2 }}>{label}</span>
      <div style={{ display:'flex', alignItems:'center', gap:4 }}>
        <span style={{ color:'#999' }}>low</span>
        <svg width="120" height="12">
          <defs><linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="rgb(255,255,255)"/>
            <stop offset="100%" stopColor="rgb(63,84,196)"/>
          </linearGradient></defs>
          <rect x="0" y="0" width="120" height="12" fill={`url(#${gradId})`} rx="3"/>
        </svg>
        <span style={{ color:'rgb(63,84,196)', fontWeight:600 }}>high</span>
      </div>
    </div>
  );
}

// ─── Tooltip ──────────────────────────────────────────────────────────────────
function NodeTooltip({ node, pos, colorMode }) {
  if (!node) return null;
  const isLigand = node.nodeType === 'ligand';

  if (isLigand) {
    const bgColor = getAnnotationColor(node.annotation);
    return (
      <div style={{
        position:'absolute', left:pos.x+14, top:pos.y-12,
        background:'#fff', border:`1.5px solid ${bgColor}`,
        borderRadius:8, padding:'8px 12px',
        pointerEvents:'none', zIndex:100, minWidth:155,
        boxShadow:'0 2px 12px rgba(0,0,0,0.12)',
      }}>
        <div style={{ fontFamily:'sans-serif', fontSize:13, fontWeight:700, color:bgColor }}>
          {node.ligandName}
        </div>
        {node.pdbId && <div style={{ fontSize:11, color:'#666', marginTop:2 }}>PDB: {node.pdbId}</div>}
        <div style={{
          display:'inline-flex', alignItems:'center', gap:5, marginTop:6,
          fontSize:11, background:`${bgColor}22`, border:`1px solid ${bgColor}88`,
          borderRadius:12, padding:'2px 8px', color:bgColor, fontWeight:600,
        }}>
          <span style={{ width:7, height:7, borderRadius:'50%', background:bgColor, flexShrink:0 }} />
          {node.annotation}
        </div>
      </div>
    );
  }

  const cat    = node.category || 'Non-polar';
  const color  = colorMode==='category' ? CATEGORY_COLORS[cat]||'#f4d03f'
               : colorMode==='hydrophobicity' ? kdToColor(node.aa)
               : metricToColor(node.metricNorm ?? 0);
  const border = colorMode==='category' ? CATEGORY_BORDER_COLORS[cat]||'#c9a800'
               : colorMode==='hydrophobicity' ? kdToBorderColor(node.aa)
               : metricBorderColor(node.metricNorm ?? 0);
  const kd = KD_SCALE[node.aa];
  return (
    <div style={{
      position:'absolute', left:pos.x+14, top:pos.y-12,
      background:'#fff', border:`1.5px solid ${color}`,
      borderRadius:8, padding:'8px 12px',
      pointerEvents:'none', zIndex:100, minWidth:175,
      boxShadow:'0 2px 12px rgba(0,0,0,0.12)',
    }}>
      <div style={{ fontFamily:'ui-monospace, monospace', fontSize:13, fontWeight:700, color:border }}>
        {node.aa} {node.pos}
      </div>
      <div style={{ fontSize:11, color:'#666', marginTop:2 }}>Chain {node.chain}</div>
      {node.seqPos != null && (
        <div style={{ fontSize:11, marginTop:3, display:'flex', alignItems:'center', gap:4 }}>
          <span style={{ color:'#999' }}>UniProt pos:</span>
          <span style={{ fontFamily:'ui-monospace,monospace', fontWeight:700, color:border,
            background:`${color}18`, borderRadius:4, padding:'0px 5px' }}>
            {node.seqPos}
          </span>
          {node.seqPos !== node.pos && (
            <span style={{ fontSize:10, color:'#bbb' }}>≠ struct. {node.pos}</span>
          )}
        </div>
      )}
      <div style={{ display:'flex', flexDirection:'column', gap:3, marginTop:6 }}>
        <div style={{
          display:'inline-flex', alignItems:'center', gap:5, fontSize:11,
          background:`${CATEGORY_COLORS[cat]}22`, border:`1px solid ${CATEGORY_COLORS[cat]}88`,
          borderRadius:12, padding:'2px 8px', color:CATEGORY_BORDER_COLORS[cat], fontWeight:600,
        }}>
          <span style={{ width:7, height:7, borderRadius:'50%', background:CATEGORY_COLORS[cat], flexShrink:0 }} />
          {CATEGORY_LABEL[cat]}
        </div>
        {kd !== undefined && (
          <div style={{
            display:'inline-flex', alignItems:'center', gap:5, fontSize:11,
            background:`${kdToColor(node.aa)}33`, border:`1px solid ${kdToBorderColor(node.aa)}55`,
            borderRadius:12, padding:'2px 8px', color:kdToBorderColor(node.aa), fontWeight:600,
          }}>
            <span style={{ width:7, height:7, borderRadius:'50%', background:kdToColor(node.aa), flexShrink:0 }} />
            KD: {kd>0?'+':''}{kd.toFixed(1)}
          </div>
        )}
        {(node.metricDeg > 0) && (
          <div style={{ fontSize:10, color:'#555', marginTop:2, display:'flex', flexDirection:'column', gap:1 }}>
            <span>Degree: <b>{node.metricDeg}</b></span>
            <span>Betweenness: <b>{node.metricBC?.toFixed(4)}</b></span>
            <span>Closeness: <b>{node.metricCC?.toFixed(4)}</b></span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const LigandNetwork = ({ ligandData = [], id = 'kd-bar-ligand' }) => {
  const cyRef        = useRef(null);
  const containerRef = useRef(null);
  const [layout,      setLayout]      = useState('fcose');
  const [colorMode,   setColorMode]   = useState('category');
  const [distMax,     setDistMax]     = useState(8);
  const [tooltip,     setTooltip]     = useState(null);
  const [tooltipPos,  setTooltipPos]  = useState({ x:0, y:0 });
  const [selectedCat, setSelectedCat] = useState(null);
  const [stats,       setStats]       = useState({ residues:0, ligands:0, edges:0, shown:0 });
  const [metrics,     setMetrics]     = useState([]);
  const [showMetrics, setShowMetrics] = useState(false);
  const [computing,   setComputing]   = useState(false);

  const allElements = useMemo(() => buildElements(ligandData), [ligandData]);

  // Reset metrics whenever the structure changes (ligandData changes)
  useEffect(() => {
    setMetrics([]);
    setShowMetrics(false);
    setComputing(false);
    // Reset color mode to category if it was a metric mode
    setColorMode(prev => ['betweenness','closeness','degree'].includes(prev) ? 'category' : prev);
  }, [ligandData]);

  useEffect(() => {
    const residues = allElements.filter(e => !e.data.source && e.data.nodeType==='residue').length;
    const ligands  = allElements.filter(e => !e.data.source && e.data.nodeType==='ligand').length;
    const edges    = allElements.filter(e =>  e.data.source).length;
    setStats({ residues, ligands, edges, shown: edges });
  }, [allElements]);

  useEffect(() => {
    if (cyRef.current) cyRef.current.layout(getLayoutConfig(layout)).run();
  }, [layout]);

  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    let shown = 0;
    cy.edges().forEach(e => {
      if (e.data('distance') <= distMax) { e.removeClass('hidden'); shown++; }
      else e.addClass('hidden');
    });
    setStats(s => ({ ...s, shown }));
  }, [distMax]);

  useEffect(() => {
    if (cyRef.current) cyRef.current.setStyle(buildStylesheet(colorMode));
  }, [colorMode]);

  // When metric color mode changes, refresh metricNorm on nodes
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || metrics.length === 0) return;
    if (!['betweenness','closeness','degree'].includes(colorMode)) return;
    cy.nodes().filter(n => n.data('nodeType') === 'residue').forEach(n => {
      const m = metrics.find(r => r.id === n.id());
      if (!m) return;
      n.data('metricNorm', colorMode==='betweenness' ? m.normBC : colorMode==='closeness' ? m.normCC : m.normDeg);
    });
    cy.setStyle(buildStylesheet(colorMode));
  }, [colorMode, metrics]);

  const computeMetrics = useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;
    setComputing(true);
    setTimeout(() => {
      try {
        const resNodes = cy.nodes().filter(n => n.data('nodeType') === 'residue');

        // Degree — simple count of visible edges per residue node
        const degMap = {};
        resNodes.forEach(n => { degMap[n.id()] = n.connectedEdges().not('.hidden').length; });
        const allDeg = resNodes.map(n => degMap[n.id()] || 0);
        const maxDeg = Math.max(...allDeg, 1);

        // Betweenness & Closeness: need full graph (all nodes + visible edges) so
        // internal Cytoscape index maps stay consistent — then query only resNodes
        const allNodes   = cy.nodes();
        const visEdges   = cy.edges().not('.hidden');
        const fullSubgraph = allNodes.union(visEdges);

        let bc, cc;
        const canRunBC = fullSubgraph.nodes().length >= 2 && fullSubgraph.edges().length >= 1;

        if (canRunBC) {
          bc = fullSubgraph.betweennessCentrality({ weight: e => 1/(e.data('distance')||1), directed:false });
          cc = fullSubgraph.closenessCentralityNormalized({ directed: false });
        }

        const allBC  = canRunBC ? resNodes.map(n => bc.betweenness(n)) : resNodes.map(() => 0);
        const allCC  = canRunBC ? resNodes.map(n => cc.closeness(n))   : resNodes.map(() => 0);
        const maxBC  = Math.max(...allBC, 1);
        const maxCC  = Math.max(...allCC, 1);

        const result = [];
        resNodes.forEach(n => {
          const bcVal  = canRunBC ? bc.betweenness(n) : 0;
          const ccVal  = canRunBC ? cc.closeness(n)   : 0;
          const degVal = degMap[n.id()] || 0;
          const normBC = bcVal/maxBC, normCC = ccVal/maxCC, normDeg = degVal/maxDeg;
          n.data('metricBC',   bcVal);
          n.data('metricCC',   ccVal);
          n.data('metricDeg',  degVal);
          n.data('metricNorm', normBC);
          result.push({ id:n.id(), label:n.data('label'), aa:n.data('aa'), bc:bcVal, cc:ccVal, deg:degVal, normBC, normCC, normDeg });
        });

        setMetrics(result);
        setShowMetrics(true);

        if (['betweenness','closeness','degree'].includes(colorMode)) {
          resNodes.forEach(n => {
            const m = result.find(r => r.id === n.id());
            if (!m) return;
            n.data('metricNorm', colorMode==='betweenness' ? m.normBC : colorMode==='closeness' ? m.normCC : m.normDeg);
          });
          cy.setStyle(buildStylesheet(colorMode));
        }
      } catch(err) {
        console.error('[LigandNetwork] Metrics error:', err);
        alert(`Metrics error: ${err.message}`);
      } finally {
        setComputing(false);
      }
    }, 80);
  }, [colorMode]);

  const applyCategoryFilter = useCallback((cy, cat) => {
    cy.elements().addClass('dimmed');
    const match = cy.nodes().filter(n => n.data('category') === cat);
    match.removeClass('dimmed').addClass('highlighted');
    match.connectedEdges().not('.hidden').removeClass('dimmed').addClass('highlighted');
    // also highlight connected ligand nodes
    match.neighborhood('node').removeClass('dimmed').addClass('highlighted');
  }, []);

  const handleCyReady = useCallback((cy) => {
    cyRef.current = cy;
    cy.on('mouseover', 'node', (e) => {
      const node = e.target;
      setTooltip(node.data());
      setTooltipPos(node.renderedPosition());
      cy.elements().addClass('dimmed');
      node.removeClass('dimmed').addClass('highlighted');
      node.connectedEdges().not('.hidden').removeClass('dimmed').addClass('highlighted');
      node.neighborhood('node').removeClass('dimmed').addClass('highlighted');
    });
    cy.on('mouseout', 'node', () => {
      setTooltip(null);
      if (selectedCat) applyCategoryFilter(cy, selectedCat);
      else cy.elements().removeClass('dimmed highlighted');
    });
    cy.on('tap', (e) => {
      if (e.target === cy) {
        setSelectedCat(null);
        cy.elements().removeClass('dimmed highlighted');
      }
    });
  }, [selectedCat, applyCategoryFilter]);

  const handleCategoryClick = (cat) => {
    const cy = cyRef.current;
    if (!cy) return;
    if (selectedCat === cat) { setSelectedCat(null); cy.elements().removeClass('dimmed highlighted'); }
    else { setSelectedCat(cat); applyCategoryFilter(cy, cat); }
  };

  const handleFitView   = () => cyRef.current?.fit(undefined, 30);
  const handleRelayout  = () => { if (cyRef.current) cyRef.current.layout(getLayoutConfig(layout)).run(); };
  const handleExportPNG = () => {
    if (!cyRef.current) return;
    const png = cyRef.current.png({ scale: 2, bg: '#ffffff' });
    const a = document.createElement('a'); a.href = png;
    a.download = `ligand-network.png`; a.click();
  };

  if (!ligandData || ligandData.length === 0) return null;

  const isHydro   = colorMode === 'hydrophobicity';
  const isMetric  = ['betweenness','closeness','degree'].includes(colorMode);
  const metricLabel = colorMode==='betweenness' ? 'Betweenness Centrality'
                    : colorMode==='closeness'    ? 'Closeness Centrality'
                    : 'Degree';

  return (
    <div style={{ marginBottom: '2rem' }}>

      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h2 className="display-6 fw-bold mb-1">Ligand–Residue Network</h2>
          <div className="d-flex gap-3" style={{ fontSize:12, color:'#666' }}>
            <span><b>{stats.residues}</b> residues</span>
            <span><b>{stats.ligands}</b> ligands</span>
            <span><b>{stats.shown}</b> / <b>{stats.edges}</b> contacts shown</span>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Dropdown value={layout} options={LAYOUT_OPTIONS} onChange={e => setLayout(e.value)} style={{ width:210 }} className="p-inputtext-sm" />
          <Button icon="pi pi-refresh"     tooltip="Re-run layout" tooltipOptions={{ position:'top' }} onClick={handleRelayout}  text size="small" />
          <Button icon="pi pi-search-plus" tooltip="Fit view"      tooltipOptions={{ position:'top' }} onClick={handleFitView}   text size="small" />
          <Button icon="pi pi-download"    tooltip="Export PNG"    tooltipOptions={{ position:'top' }} onClick={handleExportPNG} text size="small" />
          <Button
            icon={computing ? 'pi pi-spin pi-spinner' : 'pi pi-chart-line'}
            label="Compute metrics"
            tooltip="Betweenness, closeness &amp; degree centrality for residue nodes"
            tooltipOptions={{ position:'top' }}
            onClick={computeMetrics}
            disabled={computing}
            size="small"
            outlined
          />
        </div>
      </div>

      {/* Controls */}
      <div className="d-flex align-items-center flex-wrap gap-4 mb-3 p-3"
        style={{ background:'#f8f8f8', borderRadius:10, border:'1px solid #e8e8e8' }}>
        <div className="d-flex align-items-center gap-2">
          <span style={{ fontSize:12, fontWeight:600, color:'#444', whiteSpace:'nowrap' }}>Residue color</span>
          <div style={{ display:'flex', flexWrap:'wrap', borderRadius:8, overflow:'hidden', border:'1px solid #ddd' }}>
            {[
              { value:'category',       label:'Category'        },
              { value:'hydrophobicity', label:'Hydrophobicity'  },
              { value:'betweenness',    label:'Betweenness'     },
              { value:'closeness',      label:'Closeness'       },
              { value:'degree',         label:'Degree'          },
            ].map(opt => {
              const needsMetrics = ['betweenness','closeness','degree'].includes(opt.value);
              const disabled     = needsMetrics && metrics.length === 0;
              return (
                <button key={opt.value} onClick={() => !disabled && setColorMode(opt.value)}
                  title={disabled ? 'Click "Compute metrics" first' : ''}
                  style={{
                    padding:'5px 11px', fontSize:11, cursor:disabled?'not-allowed':'pointer',
                    background: colorMode===opt.value ? '#333' : disabled ? '#f0f0f0' : '#fff',
                    color:      colorMode===opt.value ? '#fff' : disabled ? '#bbb'    : '#555',
                    border:'none', borderRight:'1px solid #ddd',
                    fontWeight: colorMode===opt.value ? 600 : 400, transition:'all 0.15s',
                  }}>
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2" style={{ flex:1, minWidth:220 }}>
          <span style={{ fontSize:12, fontWeight:600, color:'#444', whiteSpace:'nowrap' }}>Distance filter</span>
          <input type="range" min={3.5} max={8} step={0.1} value={distMax}
            onChange={e => setDistMax(parseFloat(e.target.value))}
            style={{ flex:1, accentColor:'#555' }} />
          <span style={{ fontSize:12, fontWeight:700, color:'#333', minWidth:50, textAlign:'right', fontFamily:'ui-monospace, monospace' }}>
            ≤ {distMax.toFixed(1)} Å
          </span>
        </div>
      </div>

      {/* Canvas */}
      <div ref={containerRef} style={{
        position:'relative', borderRadius:10, overflow:'hidden',
        background:'#ffffff', border:'1px solid #e0e0e0',
        boxShadow:'0 1px 4px rgba(0,0,0,0.06)',
      }}>
        <CytoscapeComponent
          elements={allElements}
          stylesheet={buildStylesheet(colorMode)}
          layout={getLayoutConfig(layout)}
          style={{ width:'100%', height:480 }}
          cy={handleCyReady}
          userZoomingEnabled userPanningEnabled
          boxSelectionEnabled={false}
          minZoom={0.2} maxZoom={4}
        />
        {tooltip && <NodeTooltip node={tooltip} pos={tooltipPos} colorMode={colorMode} />}

        {/* Edge legend */}
        <div style={{
          position:'absolute', bottom:12, right:12,
          background:'rgba(255,255,255,0.94)', border:'1px solid #e0e0e0',
          borderRadius:8, padding:'8px 12px', fontSize:11, color:'#444',
          boxShadow:'0 1px 6px rgba(0,0,0,0.08)',
        }}>
          <div style={{ marginBottom:5, fontWeight:600, color:'#222' }}>Edge distance</div>
          {[3.5, 5, 6.5, 8].map(d => (
            <div key={d} className="d-flex align-items-center gap-2" style={{ marginBottom:3 }}>
              <svg width="34" height="6" style={{ flexShrink:0 }}>
                <line x1="0" y1="3" x2="34" y2="3"
                  stroke={distToEdgeColor(d)} strokeWidth={distToWidth(d)}
                  strokeOpacity={distToOpacity(d)} strokeLinecap="round" />
              </svg>
              <span>{d} Å</span>
            </div>
          ))}
        </div>

        {/* Node shape legend */}
        <div style={{
          position:'absolute', bottom:12, left:12,
          background:'rgba(255,255,255,0.94)', border:'1px solid #e0e0e0',
          borderRadius:8, padding:'8px 12px', fontSize:11, color:'#444',
          boxShadow:'0 1px 6px rgba(0,0,0,0.08)',
        }}>
          <div style={{ marginBottom:5, fontWeight:600, color:'#222' }}>Node type</div>
          <div className="d-flex align-items-center gap-2" style={{ marginBottom:3 }}>
            <svg width="14" height="14"><ellipse cx="7" cy="7" rx="6" ry="6" fill="#f4d03f" stroke="#c9a800" strokeWidth="1.5"/></svg>
            <span>Residue</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <svg width="14" height="14"><polygon points="7,1 13,7 7,13 1,7" fill={ANNOTATION_COLORS['drug-like']} stroke={ANNOTATION_COLORS['drug-like']} strokeWidth="1.5"/></svg>
            <span>Ligand</span>
          </div>
        </div>
      </div>

      {/* Legend below */}
      {isHydro ? (
        <div className="mt-3"><KDColorbar id={id} /></div>
      ) : isMetric ? (
        <div className="mt-3"><MetricColorbar label={metricLabel} id={id} /></div>
      ) : (
        <div className="d-flex flex-wrap gap-2 mt-3 align-items-start">
          {/* Residue category legend */}
          <div className="d-flex flex-wrap gap-2">
            {Object.entries(CATEGORY_COLORS).map(([cat, color]) => {
              const isActive = selectedCat === cat;
              const border   = CATEGORY_BORDER_COLORS[cat];
              return (
                <button key={cat} onClick={() => handleCategoryClick(cat)} style={{
                  display:'flex', alignItems:'center', gap:6,
                  background: isActive ? `${color}22` : '#f8f8f8',
                  border: `1px solid ${isActive ? border : '#ddd'}`,
                  borderRadius:20, padding:'4px 10px', cursor:'pointer',
                  fontSize:11, color: isActive ? border : '#555',
                  fontWeight: isActive ? 600 : 400, transition:'all 0.15s',
                }}>
                  <span style={{ width:10, height:10, borderRadius:'50%', background:color, border:`1.5px solid ${border}`, flexShrink:0 }} />
                  <span>{CATEGORY_LABEL[cat]}</span>
                  <span style={{ opacity:0.5, fontSize:10 }}>{CATEGORY_AAS[cat].join(' ')}</span>
                </button>
              );
            })}
          </div>
          {/* Ligand annotation legend */}
          <div style={{ width:'1px', background:'#ddd', margin:'0 4px' }} />
          <div className="d-flex flex-wrap gap-2">
            {Object.entries(ANNOTATION_COLORS).map(([ann, color]) => (
              <div key={ann} style={{
                display:'flex', alignItems:'center', gap:6,
                background:`${color}22`, border:`1px solid ${color}88`,
                borderRadius:20, padding:'4px 10px', fontSize:11, color:color,
              }}>
                <svg width="10" height="10"><polygon points="5,0 10,5 5,10 0,5" fill={color} /></svg>
                <span style={{ textTransform:'capitalize' }}>{ann}</span>
              </div>
            ))}
          </div>
          {selectedCat && (
            <button onClick={() => { setSelectedCat(null); cyRef.current?.elements().removeClass('dimmed highlighted'); }}
              style={{ background:'transparent', border:'1px solid #ccc', borderRadius:20, padding:'4px 10px', cursor:'pointer', fontSize:11, color:'#999' }}>
              ✕ Clear
            </button>
          )}
        </div>
      )}

      {/* ── Numbering notice ── */}
      <div style={{
        marginTop:10, padding:'8px 12px',
        background:'#fffbea', border:'1px solid #f4d03f',
        borderRadius:8, fontSize:11, color:'#7d6608',
        display:'flex', alignItems:'flex-start', gap:8,
      }}>
        <span style={{ fontSize:14, flexShrink:0 }}>⚠️</span>
        <span>
          Node labels follow <b>3D structure numbering</b> (PDB/AlphaFold chain), which may differ
          from the UniProt sequence position used in the other charts.
          Hover any residue to see its <b>UniProt sequence position</b> in the tooltip.
        </span>
      </div>

      {/* ── Metrics panel ── */}
      {showMetrics && metrics.length > 0 && (
        <div style={{ marginTop:16 }}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span style={{ fontSize:13, fontWeight:700, color:'#333' }}>
              Residue metrics — top 10
            </span>
            <button onClick={() => setShowMetrics(false)}
              style={{ background:'transparent', border:'none', cursor:'pointer', fontSize:12, color:'#999' }}>
              ✕ hide
            </button>
          </div>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
            {[
              { key:'betweenness', label:'Betweenness Centrality', get: m => m.bc,  fmt: v => v.toFixed(4) },
              { key:'closeness',   label:'Closeness Centrality',   get: m => m.cc,  fmt: v => v.toFixed(4) },
              { key:'degree',      label:'Degree (contacts)',       get: m => m.deg, fmt: v => v           },
            ].map(({ key, label, get, fmt }) => {
              const list = [...metrics].sort((a,b) => get(b)-get(a)).slice(0,10);
              const maxV = get(list[0]) || 1;
              return (
                <div key={key} style={{ flex:'1 1 200px', minWidth:180, border:'1px solid #e8e8e8', borderRadius:10, background:'#fafafa', padding:'10px 12px' }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'#333', marginBottom:8 }}>Top 10 — {label}</div>
                  {list.map((m, i) => {
                    const val = get(m);
                    const pct = (val/maxV)*100;
                    return (
                      <div key={m.id} style={{ marginBottom:5 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, marginBottom:2 }}>
                          <span style={{ fontFamily:'ui-monospace,monospace', fontWeight:600, color:'#333' }}>{i+1}. {m.label}</span>
                          <span style={{ color:'#666' }}>{fmt(val)}</span>
                        </div>
                        <div style={{ height:5, borderRadius:3, background:'#eee', overflow:'hidden' }}>
                          <div style={{ width:`${pct}%`, height:'100%', background:'rgb(63,84,196)', borderRadius:3, transition:'width 0.3s' }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p style={{ fontSize:11, color:'#888', marginTop:6, marginBottom:0 }}>
        ◆ Ligands (diamonds) · ● Residues (circles) · Hover to highlight · Scroll to zoom · drag to pan
        {metrics.length===0 && <span> · Click <b>Compute metrics</b> to enable centrality colour modes</span>}
      </p>
    </div>
  );
};

export default LigandNetwork;
